import React, { useState, useMemo } from 'react';
import { useSales } from '../../context/SalesContext';
import { apiService } from '../../services/api';

const DEFAULT_CATEGORIES = ['Rice', 'Gravey', 'Biscuits', 'Pastries', 'Naan', 'Desi Snacks', 'Grills', 'Appetisers', 'Sweets', 'Best Sellers'];

export default function SalesEditMenu() {
  const { allShops, menuByShopId, loading, loadError } = useSales();
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', price: '', category: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', price: '', category: 'Best Sellers' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const defaultShop = useMemo(() => (allShops.length > 0 ? allShops[0] : null), [allShops]);
  const allItems = useMemo(
    () => (defaultShop ? (menuByShopId[defaultShop.id] || []) : []),
    [defaultShop, menuByShopId]
  );

  const itemsByCategory = useMemo(() => {
    const map = {};
    allItems.forEach((item) => {
      const cat = item.category || 'Best Sellers';
      if (!map[cat]) map[cat] = [];
      map[cat].push(item);
    });
    const order = [...DEFAULT_CATEGORIES];
    const rest = Object.keys(map).filter((c) => !order.includes(c)).sort();
    const sortedCats = [...order.filter((c) => map[c]?.length), ...rest];
    return { map, sortedCats };
  }, [allItems]);

  const filteredBySearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return itemsByCategory;
    const matchesItem = (item) => {
      const name = (item.name || '').toLowerCase();
      const desc = (item.description || '').toLowerCase();
      const cat = (item.category || '').toLowerCase();
      const priceStr = String(item.price ?? '');
      return name.includes(q) || desc.includes(q) || cat.includes(q) || priceStr.includes(q);
    };
    const map = {};
    itemsByCategory.sortedCats.forEach((cat) => {
      const items = (itemsByCategory.map[cat] || []).filter(matchesItem);
      if (items.length > 0) map[cat] = items;
    });
    const sortedCats = itemsByCategory.sortedCats.filter((c) => map[c]?.length);
    return { map, sortedCats };
  }, [itemsByCategory, search]);

  const openEdit = (item) => {
    if (!defaultShop) return;
    setEditing({ item });
    setEditForm({
      name: item.name || '',
      description: item.description || '',
      price: String(item.price ?? ''),
      category: item.category || 'Best Sellers',
    });
    setError('');
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm({ name: '', description: '', price: '', category: '' });
    setError('');
  };

  const openAdd = (presetCategory) => {
    setShowAdd(true);
    setAddForm({
      name: '',
      description: '',
      price: '',
      category: presetCategory || 'Best Sellers',
    });
    setError('');
  };

  const closeAdd = () => {
    setShowAdd(false);
    setAddForm({ name: '', description: '', price: '', category: 'Best Sellers' });
    setError('');
  };

  const openDeleteConfirm = (item) => {
    setDeleteConfirm({ item });
    setError('');
  };

  const closeDeleteConfirm = () => setDeleteConfirm(null);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing || !defaultShop) return;
    const price = parseFloat(editForm.price);
    if (!editForm.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (isNaN(price) || price < 0) {
      setError('Valid price is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await apiService.sales.updateMenuItem(defaultShop.id, editing.item.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        price,
        category: editForm.category.trim() || 'Best Sellers',
      });
      closeEdit();
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!defaultShop) return;
    const price = parseFloat(addForm.price);
    if (!addForm.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (isNaN(price) || price < 0) {
      setError('Valid price is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await apiService.sales.addMenuItem(defaultShop.id, {
        name: addForm.name.trim(),
        description: addForm.description.trim(),
        price,
        category: addForm.category.trim() || 'Best Sellers',
      });
      closeAdd();
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add item.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm || !defaultShop) return;
    setSaving(true);
    setError('');
    try {
      await apiService.sales.removeMenuItem(defaultShop.id, deleteConfirm.item.id);
      closeDeleteConfirm();
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete.');
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = useMemo(() => {
    const fromData = [...new Set(allItems.map((i) => i.category || 'Best Sellers'))].sort();
    const combined = [...new Set([...DEFAULT_CATEGORIES, ...fromData])];
    return combined;
  }, [allItems]);

  if (loadError) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{loadError}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2">Edit Menu</h1>
        <p className="text-gray-600 text-sm mb-4 sm:mb-6">Add, edit, or delete menu items. Organised by category. Changes apply to the New Order menu.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, category, description or price..."
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 sm:py-2 text-base text-gray-700 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            type="button"
            onClick={() => openAdd()}
            className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium whitespace-nowrap min-h-[44px] touch-manipulation"
          >
            + Add item
          </button>
        </div>

        {filteredBySearch.sortedCats.length === 0 ? (
          <p className="text-gray-500 text-sm sm:text-base">
            {itemsByCategory.sortedCats.length === 0
              ? 'No items yet. Click "Add item" to add menu items by category.'
              : 'No items match your search.'}
          </p>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {filteredBySearch.sortedCats.map((category) => {
              const items = filteredBySearch.map[category] || [];
              return (
                <div key={category} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-800">{category}</h2>
                    <button
                      type="button"
                      onClick={() => openAdd(category)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 py-2 px-3 rounded-lg border border-blue-200 min-h-[40px] touch-manipulation"
                    >
                      + Add item
                    </button>
                  </div>
                  <ul className="space-y-2">
                    {items.map((item) => (
                      <li
                        key={item.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 py-3 px-3 sm:px-4 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-800 text-base">{item.name}</span>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0">
                          <span className="text-gray-800 font-semibold text-lg tabular-nums sm:w-24 text-right">
                            SAR {Number(item.price).toFixed(2)}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(item)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium py-2 px-3 min-h-[44px] rounded-lg hover:bg-blue-50 touch-manipulation"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteConfirm(item)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium py-2 px-3 min-h-[44px] rounded-lg hover:bg-red-50 touch-manipulation"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* Edit modal */}
        {editing && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
            onClick={closeEdit}
          >
            <div
              className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4">Edit item</h2>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <input
                    type="text"
                    list="edit-category-list"
                    value={editForm.category}
                    onChange={(e) => setEditForm((p) => ({ ...p, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                    placeholder="e.g. Rice, Gravey"
                    required
                  />
                  <datalist id="edit-category-list">
                    {categoryOptions.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price}
                    onChange={(e) => setEditForm((p) => ({ ...p, price: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg font-medium"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" onClick={closeEdit} className="py-2 px-4 border rounded-lg text-gray-700">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add item modal */}
        {showAdd && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
            onClick={closeAdd}
          >
            <div
              className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4">Add item</h2>
              <form onSubmit={handleAdd} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <input
                    type="text"
                    list="add-category-list"
                    value={addForm.category}
                    onChange={(e) => setAddForm((p) => ({ ...p, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                    placeholder="e.g. Rice, Gravey"
                    required
                  />
                  <datalist id="add-category-list">
                    {categoryOptions.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={addForm.name}
                    onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                    placeholder="Item name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={addForm.description}
                    onChange={(e) => setAddForm((p) => ({ ...p, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addForm.price}
                    onChange={(e) => setAddForm((p) => ({ ...p, price: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg font-medium"
                  >
                    {saving ? 'Adding…' : 'Add'}
                  </button>
                  <button type="button" onClick={closeAdd} className="py-2 px-4 border rounded-lg text-gray-700">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete confirm modal */}
        {deleteConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
            onClick={closeDeleteConfirm}
          >
            <div
              className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-sm p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold text-gray-800 mb-2">Delete item?</h2>
              <p className="text-gray-600 text-sm mb-4">
                &quot;{deleteConfirm.item.name}&quot; will be removed. This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg font-medium"
                >
                  {saving ? 'Deleting…' : 'Delete'}
                </button>
                <button type="button" onClick={closeDeleteConfirm} className="py-2 px-4 border rounded-lg text-gray-700">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
